import { Link } from 'react-router-dom'
import type { Placing } from '../api/public'
import { withToPar } from './toPar'
import { usePlayerPaths } from './playerPaths'

/** How a recorded outcome reads in the table. A finishing place is its own column. */
const earnedLabel: Record<string, string> = {
  match_won: 'won match',
  match_lost: 'lost match',
  match_halved: 'halved match',
  match_forfeit: 'won by forfeit',
  low_net: 'low net',
  bye: 'bye',
  knockout_advance: 'advanced',
  major_winner: 'major winner',
  major_runner_up: 'major runner-up',
}

/**
 * A night's results as a table: one row per player, in the order the server
 * put them — points, then place, then net.
 *
 * # Nothing here is arithmetic
 *
 * Points and nets are exact decimal strings (Constitution III). The rows are
 * summed and ordered on the server and shown exactly as they arrive, which is
 * why this component sorts nothing and adds nothing.
 */
export function Placings({ rows, testId }: { rows: Placing[]; testId: string }) {
  const { pathFor } = usePlayerPaths()
  const anyEarned = rows.some((r) => r.earned.length > 0)
  const anyPlace = rows.some((r) => r.finish_rank !== null)
  const holes = rows.map((r) => r.holes_completed).filter((n) => n > 0)
  const uneven = holes.length > 0 && Math.min(...holes) !== Math.max(...holes)

  return (
    <div className="scroll-x">
      <table className="table placings-table" data-testid={testId}>
        <thead>
          <tr>
            {anyPlace && (
              <th scope="col" className="numeric">
                Place
              </th>
            )}
            <th scope="col">Player</th>
            {/* The strokes received on the holes played: on a nine, half-ish of
                the eighteen-hole course handicap, and the figure the round was
                actually played off. */}
            <th scope="col" className="numeric" title="Strokes received on the holes played">
              Handicap
            </th>
            <th scope="col" className="numeric">
              Gross
            </th>
            <th scope="col" className="numeric">
              Net
            </th>
            <th scope="col" className="numeric">
              Points
            </th>
            {anyEarned && <th scope="col">Result</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.player_id} data-testid={`placing-${r.player_id}`}>
              {anyPlace && (
                <td className="numeric">
                  {r.finish_rank === null ? '—' : `${r.tied ? 'T' : ''}${r.finish_rank}`}
                </td>
              )}
              <th scope="row">
                <Link to={pathFor(r.player_id)}>{r.display_name}</Link>
              </th>
              <td className="numeric">
                {r.playing_handicap ?? '—'}
                {r.playing_handicap_overridden && (
                  <abbr
                    className="mark"
                    title="Set by the commissioner, not from the handicap index"
                  >
                    *
                  </abbr>
                )}
              </td>
              <td className="numeric">{withToPar(r.gross, r.gross_to_par)}</td>
              <td className="numeric">
                {withToPar(r.net, r.net_to_par)}
                {uneven && r.net !== null && (
                  <span className="note"> thru {r.holes_completed}</span>
                )}
              </td>
              <td className="numeric strong">{r.points}</td>
              {anyEarned && (
                <td>{r.earned.map((e) => earnedLabel[e] ?? e.replace(/_/g, ' ')).join(', ')}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.some((r) => r.playing_handicap_overridden) && (
        <p className="note">
          * Handicap set by the commissioner rather than from the handicap index.
        </p>
      )}
    </div>
  )
}
